import React, { FC, type RefObject, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectIsRequiredAddressField } from '~/src/redux/lead/lead.selectors';
import { selectCurrentRegion } from '~/src/redux/geolocation/geolocation.selectors';
import { AddressField } from '~/src/components/common-components/address-field/address-field';
import {
    onSendOrganizationInfoAnalytics,
    onSendRegionInfoAnalytics,
} from '~/src/components/blocks/referral-products/organization-info-ref/organization-info.analytics';
import { ATTR_NAMES_LEAD } from '~/src/models/constants/attr-names-lead.constants';
import type { CustomInputChangeInterface } from '~/src/components/form-components/dropdown/dropdown.types';
import type { RegionsDataInterface } from '~/src/repositories/regions/regions.repository';
import type { WebAnalyticsEventCategoryEnum } from '~/src/models/enums/web-analytics.enum';
import * as Styled from './address-info-block.styles';

export interface AddressInfoBlockProps {
    regionErrorRef: RefObject<HTMLDivElement>;
    addressErrorRef: RefObject<HTMLDivElement>;
    regionsData: RegionsDataInterface[] | [];
    eventCategory: WebAnalyticsEventCategoryEnum;
    withAutoSelectRegion?: boolean;
    onAddressValidationChange?: (hasError: boolean) => void;
    onClickServiceProvisionAddress?: (suggestedData: CustomInputChangeInterface) => void;
    onClearAddress?: () => void;
    onAddressValueChange?: () => void;
}

export const AddressInfoBlock: FC<AddressInfoBlockProps> = ({
    regionErrorRef,
    addressErrorRef,
    regionsData,
    withAutoSelectRegion,
    eventCategory,
    onAddressValidationChange,
    onClickServiceProvisionAddress,
    onClearAddress,
    onAddressValueChange,
}) => {
    const isRequiredAddressField = useSelector(selectIsRequiredAddressField);
    const [hasRegionError, setHasRegionError] = useState(false);
    const currentRegion = useSelector(selectCurrentRegion);
    const [regionValue, setRegionValue] = useState(currentRegion?.name);

    useEffect(() => {
        if (currentRegion?.name && withAutoSelectRegion) {
            setRegionValue(currentRegion.name);
        }
    }, [currentRegion]);

    useEffect(() => {
        hasRegionError && onSendRegionInfoAnalytics('region', eventCategory, null, 'Регион не найден');
    }, [hasRegionError]);

    const handleClickRegions = (suggestedData: CustomInputChangeInterface) => {
        if (suggestedData?.suggestValue) {
            onSendRegionInfoAnalytics('region', eventCategory, suggestedData?.suggestValue);
        }
    };

    const handleClickServiceProvisionAddress = (suggestedData: CustomInputChangeInterface) => {
        if (suggestedData?.suggestValue) {
            onSendOrganizationInfoAnalytics('adres_predostavleniya_uslugi', eventCategory);
        }

        onClickServiceProvisionAddress?.(suggestedData);
    };

    const handleClearAddress = () => {
        onClearAddress?.();
    };

    const handleAddressValueChange = () => {
        onAddressValueChange?.();
    };

    return (
        <Styled.AddressInfoBlockWrapper>
            <Styled.RegionDropdownWrap
                name={ATTR_NAMES_LEAD.regionId}
                regionsData={regionsData}
                label={'Регион'}
                withoutSuggest={false}
                inputErrorRef={regionErrorRef}
                placeholder={'Выберите'}
                withOptionsDisplayIcon
                autocompleteWithSelect
                onSuggestedValueChange={handleClickRegions}
                setHasError={setHasRegionError}
                defaultValue={regionValue}
                noResetWithForm
                mutableDefaultValue
                required
            />
            <AddressField
                name={ATTR_NAMES_LEAD.serviceProvisionAddress}
                label={'Адрес предоставления услуги'}
                withoutSuggest={false}
                inputErrorRef={addressErrorRef}
                placeholder={'Город, улица, дом, офис'}
                required={isRequiredAddressField}
                autocompleteWithSelect
                needCheckValue
                validated
                onSuggestedValueChange={handleClickServiceProvisionAddress}
                onValidationChange={onAddressValidationChange}
                onClear={handleClearAddress}
                onValueChange={handleAddressValueChange}
            />
        </Styled.AddressInfoBlockWrapper>
    );
};
